import { Box, useTheme } from '@hover/blueprint';
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { nudge } from 'src/modules/homeownerApp/helpers/styles';
import { useHomeownerAppContext } from 'src/modules/homeownerApp/hooks';
import { HomeownerAppMenuNames } from 'src/modules/homeownerApp/providers/HomeownerAppProvider';
import { useOnboardingSplit } from 'src/modules/onboarding/hooks/useOnboardingSplit';
import { AnimatedFullWidthContainer } from 'src/modules/pro/components/FullWidthContainer';
import { Button, ButtonLink, Logo } from 'src/shared/components';
import { urlWithRedirectTo } from 'src/shared/helpers/routing';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { useDisappearingHeaderOnScroll } from 'src/shared/hooks/useDisappearingHeaderOnScroll';
import { useGlobalStorageContext } from 'src/shared/hooks/useGlobalStorage';
import { Route as Routes } from 'src/shared/route';

import type { WelcomeAction } from '../../WelcomeSheet';
import { storeWelcomeAction } from '../../WelcomeSheet';

const Header = () => {
  const mode = 'dark';
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const { zIndices } = useTheme();
  const { headerRef, headerControls } = useDisappearingHeaderOnScroll();
  const {
    setCurrentMenu,
    trackingData,
    imageFileToUpload,
    setImageFileToUpload,
  } = useHomeownerAppContext();
  /*
   * This is being used to provide the onboarding with the correct context to determine
   * whether it should use the homeowner specific mutation or the general createUser mutation
   */
  const { setHomeownerAppIsUserHomeowner, setHomeownerAppUploadFile } =
    useGlobalStorageContext();
  const history = useHistory();
  const { search } = useLocation();
  const { showReskinnedSignup } = useOnboardingSplit();

  const handleSubmit = (action: WelcomeAction) => {
    storeWelcomeAction(action);
    if (imageFileToUpload) {
      setHomeownerAppUploadFile(imageFileToUpload);
      setImageFileToUpload();
    }
    setHomeownerAppIsUserHomeowner(true);
  };

  return (
    <AnimatedFullWidthContainer
      animate={headerControls}
      as="nav"
      background="neutral.0"
      color={mode}
      height={{ base: 500, tablet: 600 }}
      position="sticky"
      ref={headerRef}
      top={0}
      zIndex={zIndices.banner}
    >
      <Box
        alignItems="center"
        flexDirection="row"
        justifyContent="space-between"
        w={1}
      >
        <Box
          alignItems="start"
          paddingTop={200}
          width={{ base: 600, tablet: 700 }}
        >
          <Logo color="inherit" />
        </Box>
        <Box gap={200} justifyContent="center">
          <Button
            colorScheme={mode}
            display={{ base: 'block', desktop: 'none' }}
            onClick={() => setCurrentMenu(HomeownerAppMenuNames.SIGN_IN)}
            optionalTrackingProperties={{
              ...trackingData,
              track_id: 'designer-landing-page-login-button',
              button_location: 'top-of-page',
            }}
            variant="ghost"
            {...nudge(500, 'right')}
          >
            {t('signUp')}
          </Button>
          <ButtonLink
            display={{ base: 'none', desktop: 'block' }}
            onClick={() => {
              handleSubmit('login');
            }}
            optionalTrackingProperties={{
              ...trackingData,
              track_id: 'log-in',
              link_location: 'top-of-designer-landing',
            }}
            to={urlWithRedirectTo(
              Routes.LOGIN.path,
              history.location.pathname + search,
            )}
            variant="ghost"
          >
            {t('logIn')}
          </ButtonLink>
          <ButtonLink
            display={{ base: 'none', desktop: 'block' }}
            onClick={() => handleSubmit('signup')}
            optionalTrackingProperties={{
              ...trackingData,
              track_id: 'sign-up',
              link_location: 'top-of-designer-landing',
            }}
            to={urlWithRedirectTo(
              showReskinnedSignup
                ? `${Routes.SIGN_UP_ONBOARDING.path}/homeowner`
                : Routes.SIGN_UP_HOMEOWNER.path,
              history.location.pathname + search,
            )}
            variant="tertiary"
          >
            {t('signUp')}
          </ButtonLink>
        </Box>
      </Box>
    </AnimatedFullWidthContainer>
  );
};

export { Header };
